<template>
  <form ref="form" class="crypto-deposit-page" :class="{ 'crypto-deposit-page--dark': $vuetify.theme.dark }" @submit.prevent="handleSubmit">
    <v-container fluid class="main crypto-deposit-container">
      <header class="crypto-deposit-heading">
        <span><v-icon>mdi-bitcoin</v-icon></span>
        <div><h1>Crypto Deposit</h1><p>Deposit securely using your preferred cryptocurrency network.</p></div>
      </header>
      <v-row>
        <v-col xl="6" lg="7" md="7" sm="7" cols="12">
          <v-card class="deposit-form crypto-deposit-form-card">
            <v-row>
              <v-col cols="12" sm="12" md="12">
                <fieldset>
                  <legend>Transfer Method</legend>
                  <v-text-field label="Crypto Transfer" disabled placeholder="Crypto Transfer" single-line solo
                    hide-details readonly class="custom-select" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" md="12">
                <fieldset>
                  <legend>Deposit to</legend>
                  <v-select v-model="selectedAccount" :items="formattedAccounts" item-text="display"
                    item-value="trading_account_no" solo append-icon="mdi-chevron-down" label="Select One"
                    class="custom-select" hide-details @change="handleAccountChange" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" md="12">
                <fieldset>
                  <legend>Select Crypto Network</legend>
                  <v-select v-model="selectedNetwork" solo :items="formattedNetworks" item-text="network"
                    item-value="renamedNetwork" append-icon="mdi-chevron-down" label="Select One" class="custom-select"
                    @change="handleNetworkChange" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" :md="currency.name !== 'USD' ? 6 : 12">
                <fieldset>
                  <legend>
                    Amount in (USD)
                    <small v-if="minTransactionUSD > 0" class="ms-2 text-muted">
                      (Min: ${{ minTransactionUSD }})
                    </small>
                  </legend>

                  <v-text-field v-model="depositAmount" label="$" type="number" single-line solo hide-details
                    class="custom-select" :min="minTransactionUSD > 0 ? minTransactionUSD : 1" :rules="amountRules" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" md="6" v-if="currency.name !== 'USD'">
                <fieldset>
                  <legend>Amount in <span :style="{ color: currency.color }">({{ currency.name }})</span></legend>
                  <v-text-field readonly v-model="currencyAmount" :label="currency.symbol" type="number" single-line
                    solo hide-details class="custom-select text-muted" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" md="12">
                <fieldset>
                  <legend>Transaction ID *</legend>
                  <v-text-field v-model="cryptoWalletId" label="Your Payment Slip Transaction ID"
                    placeholder="Your Payment Slip Transaction ID" single-line solo hide-details
                    class="custom-select" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" md="12" v-if="needSlip == 1">
                <div class="input-file">
                  <span>Upload Pay.Proof</span>
                  <v-file-input v-model="bankSlip" label="Choose file" filled prepend-icon="mdi-file-image"
                    hide-details />
                </div>
              </v-col>

              <v-col cols="12" sm="12" md="12">
                <v-btn type="submit" tile elevation="0" class="ma-0 text-capitalize btn-account d-block crypto-deposit-button"
                  :loading="loading" :disabled="!isFormValid || loading">
                  Deposit
                </v-btn>
              </v-col>

              <v-col cols="12" sm="12" md="12" class="crypto-div">
                <v-card-title class="title px-0 py-0">Instruction:</v-card-title>
                <ol class="instruction">
                  <li>Payments from third parties are not accepted. The name of the sender must match the account
                    holder's name.</li>
                  <li>Broker accepts wire transfers in USD. Funds sent in a different currency will be converted at the
                    current exchange rate.</li>
                </ol>
              </v-col>
            </v-row>
          </v-card>
        </v-col>

        <v-col xl="6" lg="5" md="5" sm="5" cols="12">
          <v-card v-if="selectedNetwork" class="elevation-0 crypto-form-card crypto-network-card mt-2">
            <v-card-text class="content">
              <h3 class="title">Broker <strong>{{ selectedNetworkName }}</strong> Address</h3>
              <span class="d-inline-block text-truncate" ref="addressText">{{ selectedNetworkAddress }}</span>
              <v-btn tile elevation="0" class="ma-0 text-capitalize btn-account d-block copy-address-button"
                @click="copyAddress">
                {{ copied ? 'Copied!' : 'Copy Address' }}
              </v-btn>
            </v-card-text>

            <v-card-text>
              <v-img :src="qrCodeUrl" alt="QR Code" width="200" height="200" contain class="crypto-qr-code">
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate color="amber darken-1"></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </form>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { CRYPTO__ACTIONS, DEPOSITMETHODS__ACTIONS } from "@/store/action-types";
import toast from "@/services/Notification";

export default {
  name: "CryptoDepositForm",

  data() {
    return {
      payment_id: null,

      //  min deposit amount (USD) from payment_settings.min_transaction
      minTransactionUSD: 0,

      selectedAccount: null,
      selectedNetwork: null,
      depositAmount: "",
      currencyAmount: "",
      cryptoWalletId: "",
      currency: {
        rate: 1,
        name: "USD",
        symbol: "$",
        color: "#fff",
      },
      bankSlip: null,
      loading: false,
      copied: false,
      baseUrl: "https://api.qrserver.com/v1/create-qr-code/",
      rules: {
        required: (value) => !!value || "This field is required",
      },
    };
  },

  computed: {
    ...mapState("crypto", ["networks"]),
    // methods added to read min_transaction by payment_id
    ...mapState("deposit", ["accounts", "needSlip", "methods"]),

    formattedAccounts() {
      return this.accounts.map((account) => ({
        display: `${account.trading_account_no} (${account.account_type_display}) ${account.Balance} ${account.currency_name}`,
        trading_account_no: account.trading_account_no,
        ...account,
      }));
    },

    selectedAccountDetails() {
      return this.accounts.find((acc) => acc.trading_account_no === this.selectedAccount);
    },

    selectedNetworkDetails() {
      return this.formattedNetworks.find((network) => network.renamedNetwork === this.selectedNetwork) || {};
    },

    formattedNetworks() {
      return (this.networks || []).map((network) => {
        const rawNetwork = network?.network;
        const rawRenamedNetwork = network?.renamedNetwork;
        const networkName =
          this.getNetworkLabel(rawNetwork) ||
          this.getNetworkLabel(rawRenamedNetwork) ||
          "";
        const renamedNetwork =
          this.getNetworkText(rawRenamedNetwork) ||
          this.getNetworkText(rawNetwork) ||
          "";

        return {
          ...network,
          network: networkName,
          renamedNetwork,
        };
      });
    },

    selectedNetworkAddress() {
      return this.selectedNetworkDetails.address || "";
    },

    selectedNetworkName() {
      return this.selectedNetworkDetails.network || "";
    },

    qrCodeUrl() {
      return this.selectedNetworkAddress
        ? `${this.baseUrl}?size=200x200&data=${encodeURIComponent(this.selectedNetworkAddress)}`
        : "";
    },

    //  rules include dynamic min deposit
    amountRules() {
      return [
        this.rules.required,
        (value) => parseFloat(value) > 0 || "Amount must be greater than 0",
        (value) =>
          !this.minTransactionUSD ||
          parseFloat(value) >= this.minTransactionUSD ||
          `Minimum deposit is $${this.minTransactionUSD}`,
      ];
    },

    isFormValid() {
      const amt = parseFloat(this.depositAmount);

      return (
        this.selectedAccount &&
        this.selectedNetwork &&
        Number.isFinite(amt) &&
        amt > 0 &&
        (this.minTransactionUSD ? amt >= this.minTransactionUSD : true) &&
        this.cryptoWalletId
      );
    },
  },

  watch: {
    depositAmount(newAmount) {
      const amt = parseFloat(newAmount);
      if (!Number.isFinite(amt)) {
        this.currencyAmount = "";
        return;
      }

      if (this.currency && this.currency.rate) {
        this.currencyAmount = (amt * this.currency.rate).toFixed(2);
      } else {
        this.currencyAmount = String(amt);
      }
    },

    //  minTransactionUSD updated when methods available
    methods: {
      handler() {
        this.pickMinTransactionFromMethods();
      },
      immediate: true,
    },
    payment_id() {
      this.pickMinTransactionFromMethods();
    },
  },

  methods: {
    ...mapActions("crypto", [CRYPTO__ACTIONS.NETWORKS]),
    ...mapActions("deposit", [
      DEPOSITMETHODS__ACTIONS.DEPOSITACCOUNTS,
      DEPOSITMETHODS__ACTIONS.DEPOSITMETHODS,
      DEPOSITMETHODS__ACTIONS.CREATECRYPTODEPOSIT,
      DEPOSITMETHODS__ACTIONS.NEEDSLIP,
    ]),

    getNetworkText(value) {
      if (!value) return "";
      if (typeof value === "string") return value;
      if (typeof value === "number") return String(value);

      return (
        value.network ||
        value.name ||
        value.label ||
        value.title ||
        value.value ||
        ""
      );
    },

    getNetworkLabel(value) {
      const network = this.getNetworkText(value);
      const labels = {
        btc: "Bitcoin",
        eth: "Ethereum Network",
        erc20: "Ethereum Network",
        trc20: "USDT TRC20",
        bep20: "USDT BEP20",
        bnb: "BNB",
      };

      return labels[network.toLowerCase()] || network;
    },

    //  pick payment_settings.min_transaction by URL payment_id
    pickMinTransactionFromMethods() {
      try {
        if (!this.payment_id || !Array.isArray(this.methods)) return;

        const method =
          this.methods.find(
            (m) =>
              String(m.id) === String(this.payment_id) ||
              String(m.payment_id) === String(this.payment_id) ||
              String(m.payment_settings_id) === String(this.payment_id)
          ) || null;

        const min =
          method?.payment_settings?.min_transaction ??
          method?.min_transaction ??
          method?.minTransaction ??
          0;

        this.minTransactionUSD = Number(min) || 0;
      } catch (e) {
        this.minTransactionUSD = 0;
      }
    },

    handleAccountChange() {
      this.depositAmount = "";
      if (this.$refs.form) {
        this.$refs.form.reset();
      }

      const account_details = this.accounts.find((acc) => acc.trading_account_no === this.selectedAccount);
      this.currencyAmount = "";

      if (account_details) {
        this.currency.rate = account_details.currency_rate;
        this.currency.name = account_details.currency_name;
        this.currency.symbol = account_details.currency_symbol;
        this.currency.color = account_details.symbol_color;
      }
    },

    handleNetworkChange() {
      this.copied = false;
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
    },

    async copyAddress() {
      if (!this.selectedNetworkAddress) return;

      try {
        await navigator.clipboard.writeText(this.selectedNetworkAddress);
        this.copied = true;
        setTimeout(() => (this.copied = false), 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
      }
    },

    //now blocks submit + includes min_transaction checks
    validateForm() {
      let ok = true;

      if (!this.selectedAccount) {
        toast.error("Please select an account");
        ok = false;
      }
      if (!this.selectedNetwork) {
        toast.error("Please select a network");
        ok = false;
      }

      const amt = parseFloat(this.depositAmount);
      if (!Number.isFinite(amt) || amt <= 0) {
        toast.error("Please enter a valid deposit amount");
        ok = false;
      }

      if (this.minTransactionUSD && Number.isFinite(amt) && amt < this.minTransactionUSD) {
        toast.error(`Minimum deposit is $${this.minTransactionUSD}`);
        ok = false;
      }

      if (!this.cryptoWalletId) {
        toast.error("Please enter Transaction ID");
        ok = false;
      }

      if (!this.bankSlip && this.needSlip == 1) {
        toast.error("Please upload payment proof");
        ok = false;
      }

      return ok;
    },

    async handleSubmit() {
      try {
        if (!this.validateForm()) return;

        this.loading = true;

        const formData = new FormData();
        formData.append("trading_account_no", this.selectedAccount);
        formData.append("amount", this.depositAmount);
        formData.append("payment_slip", this.bankSlip);
        formData.append("payment_id", this.payment_id);
        formData.append("network", this.selectedNetwork);
        formData.append("trx", this.cryptoWalletId);

        await this[DEPOSITMETHODS__ACTIONS.CREATECRYPTODEPOSIT](formData);

        this.resetForm();
      } catch (error) {
        console.error("Deposit submission error:", error);
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.$nextTick(() => {
        this.selectedAccount = null;
        this.selectedNetwork = null;
        this.depositAmount = "";
        this.currencyAmount = "";
        this.cryptoWalletId = "";
        this.bankSlip = null;
        this.copied = false;

        if (this.$refs.form) {
          this.$refs.form.reset();
        }

        const fileInput = this.$refs.fileInput;
        if (fileInput) {
          fileInput.reset();
        }
      });
    },
  },

  async mounted() {
    try {
      this.payment_id = this.$route.query.payment_id;

      await Promise.all([
        this[CRYPTO__ACTIONS.NETWORKS](),
        this[DEPOSITMETHODS__ACTIONS.DEPOSITACCOUNTS](),
        this[DEPOSITMETHODS__ACTIONS.DEPOSITMETHODS](), // needed for min_transaction
        console.log('Nte', this.crypto),
      ]);

      this.pickMinTransactionFromMethods();
    } catch (error) {
      console.error("Failed to fetch initial data:", error);
    }
  },

  async created() {
    await this[DEPOSITMETHODS__ACTIONS.NEEDSLIP]();
  },
};
</script>

<style lang="scss" scoped>
.crypto-deposit-page {
  --crypto-page: #f7f8fa;
  --crypto-panel: #fff;
  --crypto-soft: #f4f5f7;
  --crypto-field: #fff;
  --crypto-border: #dfe2e6;
  --crypto-text: #171a1f;
  --crypto-muted: #626973;
  --crypto-gold: #d99a00;
  box-sizing: border-box;
  height: calc(100vh - 64px);
  min-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
  color: var(--crypto-text);
  background: var(--crypto-page);
}
.crypto-deposit-page--dark {
  --crypto-page: #030b13;
  --crypto-panel: #07111b;
  --crypto-soft: #0b1621;
  --crypto-field: #08131e;
  --crypto-border: #293026;
  --crypto-text: #f4f5f7;
  --crypto-muted: #aeb6c0;
  --crypto-gold: #ffc400;
  background: radial-gradient(circle at 70% 14%, rgba(255, 196, 0, .05), transparent 34%), var(--crypto-page);
}
.crypto-deposit-container { box-sizing: border-box; max-width: none !important; min-height: 100%; padding: 16px 18px 24px !important; }
.crypto-deposit-heading { min-height: 64px; display: flex; align-items: center; gap: 13px; margin-bottom: 8px; }
.crypto-deposit-heading > span { width: 42px; height: 42px; display: inline-flex; align-items: center; justify-content: center; color: var(--crypto-gold); border: 1px solid rgba(217, 154, 0, .42); border-radius: 9px; background: rgba(217, 154, 0, .09); }
.crypto-deposit-heading .v-icon { color: inherit; }
.crypto-deposit-heading h1 { margin: 0 0 3px; color: var(--crypto-text); font-size: 23px; line-height: 1.2; }
.crypto-deposit-heading p { margin: 0; color: var(--crypto-muted); font-size: 13px; }
.crypto-deposit-form-card, .crypto-network-card { color: var(--crypto-text) !important; border: 1px solid var(--crypto-border); border-radius: 10px !important; background: var(--crypto-panel) !important; box-shadow: none !important; }
.crypto-deposit-form-card { padding: 12px 14px; }
.crypto-deposit-page .deposit-form fieldset { border-color: var(--crypto-border) !important; background: var(--crypto-field) !important; }
.crypto-deposit-page .deposit-form fieldset legend { color: var(--crypto-text) !important; background: var(--crypto-panel) !important; }
.crypto-deposit-page::v-deep .deposit-form fieldset .custom-select .v-input__slot { border-color: transparent !important; background: var(--crypto-field) !important; }
.crypto-deposit-page::v-deep .deposit-form fieldset .custom-select input,
.crypto-deposit-page::v-deep .deposit-form fieldset .custom-select .v-select__selection { color: var(--crypto-text) !important; }
.crypto-deposit-page::v-deep .deposit-form fieldset .custom-select .v-label,
.crypto-deposit-page::v-deep .deposit-form fieldset .custom-select input::placeholder,
.crypto-deposit-page::v-deep .deposit-form .input-file .v-label { color: var(--crypto-muted) !important; opacity: .78; }
.crypto-deposit-page::v-deep .deposit-form fieldset .custom-select .v-icon { color: var(--crypto-gold) !important; }
.crypto-deposit-page .deposit-form .input-file > span { color: var(--crypto-text) !important; }
.crypto-deposit-page::v-deep .deposit-form .input-file .v-input { color: var(--crypto-gold) !important; background: var(--crypto-field) !important; }
.crypto-deposit-page::v-deep .deposit-form .input-file .v-input__slot { background: transparent !important; }
.crypto-deposit-page::v-deep .deposit-form .input-file .v-icon,
.crypto-deposit-page::v-deep .deposit-form .input-file .v-file-input__text { color: var(--crypto-gold) !important; }
.crypto-deposit-page legend small { color: var(--crypto-gold) !important; }
.crypto-deposit-button { width: 100%; height: 44px !important; color: #17130a !important; border-radius: 6px !important; background: linear-gradient(135deg, #ffd43b, #ffc400) !important; font-weight: 700; }
.crypto-deposit-button::v-deep .v-icon { color: #17130a !important; }
.crypto-div { min-height: 0; margin-top: 4px; color: var(--crypto-muted); }
.crypto-div .title { color: var(--crypto-gold) !important; font-size: 14px !important; font-weight: 700; }
.instruction { margin: 10px 0 0; padding: 14px 18px 14px 34px; border: 1px solid var(--crypto-border); border-radius: 7px; background: var(--crypto-soft); font-size: 12px; line-height: 1.65; }
.instruction li + li { margin-top: 6px; }
.crypto-network-card { height: auto; min-height: 420px; overflow: hidden; }
.crypto-network-card .content { color: var(--crypto-text) !important; border-bottom: 1px solid var(--crypto-border); }
.crypto-network-card .content .title { margin-bottom: 12px; color: var(--crypto-text) !important; font-size: 16px !important; }
.crypto-network-card .content .title strong { color: var(--crypto-gold); }
.crypto-network-card .content > span { display: block !important; max-width: 100%; margin-bottom: 14px; padding: 10px 12px; overflow: visible; color: var(--crypto-muted); border: 1px solid var(--crypto-border); border-radius: 6px; background: var(--crypto-soft); font-family: monospace; text-align: center; text-overflow: clip; white-space: normal; overflow-wrap: anywhere; word-break: break-all; }
.copy-address-button { min-width: 126px !important; color: var(--crypto-gold) !important; border: 1px solid var(--crypto-gold); border-radius: 6px !important; background: transparent !important; font-weight: 700; }
.crypto-deposit-page--dark .crypto-network-card .copy-address-button { color: #ffc400 !important; border: 1px solid #ffc400 !important; background: rgba(255, 196, 0, .045) !important; box-shadow: inset 0 0 0 1px rgba(255, 196, 0, .08) !important; }
.crypto-deposit-page--dark .crypto-network-card .copy-address-button::v-deep .v-btn__content,
.crypto-deposit-page--dark .crypto-network-card .copy-address-button::v-deep .v-icon { color: #ffc400 !important; opacity: 1 !important; }
.crypto-deposit-page--dark .crypto-network-card .copy-address-button:hover { background: rgba(255, 196, 0, .12) !important; }
.crypto-network-card > .v-card__text:last-child { display: flex; align-items: center; justify-content: center; padding: 24px; }
.crypto-network-card .crypto-qr-code { box-sizing: content-box; width: 200px !important; min-width: 200px !important; max-width: 200px !important; height: 200px !important; min-height: 200px !important; max-height: 200px !important; flex: 0 0 200px !important; padding: 8px; overflow: hidden; border: 1px solid var(--crypto-border); border-radius: 8px; background: #fff; }
.crypto-network-card .crypto-qr-code::v-deep .v-image__image { background-position: center !important; background-size: contain !important; }
.trade {
  margin-left: -15px;
  font-weight: 300;
  font-size: 13px;
}

.main {
  height: auto !important;
  overflow-y: visible;
  margin: 0;
}

.text-muted {
  opacity: 0.7;
}
@media (max-width: 760px) {
  .crypto-deposit-page { height: calc(100vh - 56px); height: calc(100dvh - 56px); }
  .crypto-deposit-container { padding: 12px 10px 20px !important; }
  .crypto-deposit-container > .row { margin-right: -6px; margin-left: -6px; }
  .crypto-deposit-container > .row > [class*="col-"] { width: 100%; max-width: 100%; flex: 0 0 100%; padding: 6px; }
  .crypto-deposit-heading p { display: none; }
  .crypto-deposit-heading h1 { font-size: 21px; }
  .crypto-deposit-form-card { padding: 7px; }
  .crypto-deposit-form-card > .row { margin-right: -6px; margin-left: -6px; }
  .crypto-deposit-form-card > .row > [class*="col-"] { padding: 6px; }
  .crypto-deposit-button, .copy-address-button { width: 100%; }
  .crypto-div { margin-top: 4px; }
  .instruction { padding: 12px 12px 12px 28px; }
  .crypto-network-card { min-height: 0; margin-top: 6px !important; }
  .crypto-network-card .content { padding: 16px; text-align: left; }
  .crypto-network-card .content .title { line-height: 1.45; }
  .crypto-network-card .content > span { padding: 10px; text-align: left; }
  .crypto-network-card > .v-card__text:last-child { padding: 18px 12px; }
  .crypto-network-card .crypto-qr-code { width: 180px !important; min-width: 180px !important; max-width: 180px !important; height: 180px !important; min-height: 180px !important; max-height: 180px !important; flex-basis: 180px !important; }
}
@media (max-width: 380px) {
  .crypto-deposit-heading > span { width: 38px; height: 38px; }
  .crypto-deposit-heading h1 { font-size: 19px; }
  .crypto-network-card .crypto-qr-code { width: 160px !important; min-width: 160px !important; max-width: 160px !important; height: 160px !important; min-height: 160px !important; max-height: 160px !important; flex-basis: 160px !important; }
}
</style>
